import React, { useEffect, useState } from "react";
import {
  ChevronDownIcon,
  ChevronRightIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { formatCurrency } from "../../../shared/utility";
import {
  getSplitRemainder,
  getSplitUnitLabel,
} from "../../../shared/orderItemPriceSplits";
import DeleteConfirmationForm from "../DeleteConfirmationForm";
import OrderItemPriceSplitModal from "./OrderItemPriceSplitModal";

export default function OrderItemPriceSplitsTable({
  orderItem,
  canEdit,
  setEmptyModelOpen,
  onCreate,
  onUpdate,
  onDelete,
  initiallyExpanded = false,
}) {
  const [expanded, setExpanded] = useState(initiallyExpanded);

  useEffect(() => {
    if (initiallyExpanded) {
      setExpanded(true);
    }
  }, [initiallyExpanded]);
  const splits = orderItem.price_splits ?? [];
  const remainder = getSplitRemainder(orderItem);
  const shippedQuantity = Number(
    orderItem?.shipment_order_item?.shipped_quantity ??
      orderItem?.shipped_amount ??
      0,
  );
  const allocatedQuantity = Number(orderItem?.allocated_quantity ?? 0);
  const overallocated = Math.max(0, allocatedQuantity - shippedQuantity);
  const unitLabel = getSplitUnitLabel(orderItem);

  const openEditor = (split = null) => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <OrderItemPriceSplitModal
          orderItem={orderItem}
          split={split}
          setOpen={setEmptyModelOpen}
          onConfirm={async (payload) => {
            const result = split
              ? await onUpdate(orderItem.id, split.id, payload)
              : await onCreate(orderItem.id, payload);
            if (result?.success) {
              setEmptyModelOpen({ childComponent: null, open: false });
              setExpanded(true);
            }
          }}
        />
      ),
    });
  };

  const confirmDelete = (split) => {
    setEmptyModelOpen({
      open: true,
      childComponent: (
        <DeleteConfirmationForm
          title="Delete price split?"
          buttonMessage="Delete split"
          message={`Delete ${split.quantity} ${unitLabel} at ${formatCurrency(split.unit_price)}?`}
          setOpen={setEmptyModelOpen}
          confirmAction={async () => {
            const result = await onDelete(orderItem.id, split.id);
            if (result?.success) {
              setEmptyModelOpen({ childComponent: null, open: false });
            }
          }}
        />
      ),
    });
  };

  if (!splits.length && !canEdit) return null;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-800"
        >
          {expanded ? (
            <ChevronDownIcon className="h-4 w-4" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          )}
          Price splits
          <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-slate-500 ring-1 ring-slate-200">
            {splits.length}
          </span>
        </button>
        {canEdit && remainder > 0 ? (
          <button
            type="button"
            onClick={() => openEditor()}
            className="inline-flex items-center gap-1 text-sm font-semibold text-sky-700 hover:text-sky-900"
          >
            <PlusIcon className="h-4 w-4" /> Add split
          </button>
        ) : null}
      </div>
      {expanded ? (
        <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200 bg-white">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2">Quantity</th>
                <th className="px-3 py-2">Unit price</th>
                <th className="px-3 py-2 text-right">Total</th>
                {canEdit ? <th className="px-3 py-2" /> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {splits.map((split) => (
                <tr key={split.id}>
                  <td className="px-3 py-2">
                    {split.quantity} {unitLabel}
                  </td>
                  <td className="px-3 py-2">
                    {formatCurrency(split.unit_price)}
                  </td>
                  <td className="px-3 py-2 text-right font-medium">
                    {formatCurrency(split.total_dollar_amount)}
                  </td>
                  {canEdit ? (
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          title="Edit split"
                          onClick={() => openEditor(split)}
                          className="text-sky-700"
                        >
                          <PencilSquareIcon className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Delete split"
                          onClick={() => confirmDelete(split)}
                          className="text-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
              {remainder > 0 ? (
                <tr className="bg-amber-50/60 text-slate-600">
                  <td className="px-3 py-2">
                    {remainder} {unitLabel}{" "}
                    <span className="text-xs">(unpriced)</span>
                  </td>
                  <td className="px-3 py-2">{formatCurrency(0)}</td>
                  <td className="px-3 py-2 text-right">{formatCurrency(0)}</td>
                  {canEdit ? <td /> : null}
                </tr>
              ) : null}
              {overallocated > 0 ? (
                <tr className="bg-red-50 text-red-700">
                  <td
                    className="px-3 py-2 font-medium"
                    colSpan={canEdit ? 4 : 3}
                  >
                    Pricing exceeds the current shipment by {overallocated}{" "}
                    {unitLabel}. Adjust the splits before marking shipped.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
